import { Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Input, Label } from '@bia-auction/ui';
import { AlertCircle } from 'lucide-react';
import { getLang, t } from '@bia-auction/shared-i18n';
import { Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Turnstile } from '../components/Turnstile';
import { useAuth } from '../lib/auth';
import { initialTurnstileToken, turnstileConfigured } from '../lib/turnstile';

/**
 * KVKK document-set version accepted by this build. Bumped when the copy changes so an
 * acceptance is traceable to the exact text (the API stores it per document).
 *
 * The linked documents are **generated from `docs/legal/*.md`** into `public/kvkk/` by
 * `scripts/build-legal.mjs`, which runs on `dev` and `build` — so the page an applicant reads
 * is the markdown, not a copy of it. They are still **drafts pending legal review**, and both
 * render behind a TASLAK banner saying so; the version bump and the review are a documented
 * operator action in `TODO.md` §4 (ADR 0020).
 */
const KVKK_VERSION = 'v1';

export function RegisterPage() {
  const { state, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    nameFirst: '',
    nameLast: '',
    email: '',
    password: '',
    mobile: '',
  });
  const [aydinlatma, setAydinlatma] = useState(false);
  const [acikRiza, setAcikRiza] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(initialTurnstileToken());
  const [turnstileReset, setTurnstileReset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Registration produces an application, not a session (M15) — so success is a state on this
  // page, not a navigation. The applicant has nowhere to be sent: they cannot sign in yet.
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const onTurnstileToken = useCallback((tk: string) => setTurnstileToken(tk), []);

  useEffect(() => {
    if (state.authenticated) void navigate({ to: '/me' });
  }, [state.authenticated, navigate]);

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!aydinlatma || !acikRiza) {
      setError(t('bidder.register.consentRequired'));
      return;
    }
    setBusy(true);
    try {
      await register({
        email: form.email,
        password: form.password,
        nameFirst: form.nameFirst,
        nameLast: form.nameLast,
        ...(form.mobile ? { mobile: form.mobile } : {}),
        locale: getLang(), // capture the UI language so notifications are localized (M8)
        turnstileToken,
        // Both flags are guaranteed true here (guarded above) — the schema requires literal true.
        consent: { aydinlatma: true, acikRiza: true, version: KVKK_VERSION },
      });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('bidder.register.failed'));
      // The Turnstile token was consumed by the (failed) attempt — reset the widget for a fresh one.
      setTurnstileToken(initialTurnstileToken());
      setTurnstileReset((n) => n + 1);
    } finally {
      setBusy(false);
    }
  };

  // Confirmation, not a redirect: the form is gone (re-submitting would just 409 on the email)
  // and the applicant is told plainly that they cannot sign in yet and will be emailed.
  if (submitted)
    return (
      <div className="flex items-center justify-center px-4 py-10">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="font-head text-h2 font-semibold">
              {t('bidder.register.pendingTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('bidder.register.pendingBody')}</p>
            <Button asChild variant="outline" size="sm">
              <Link to="/">{t('bidder.nav.auctions')}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );

  return (
    <div className="flex items-center justify-center px-4 py-10">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="font-head text-h2 font-semibold">{t('bidder.register.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <fieldset className="space-y-3">
              <legend className="mb-1 text-xs font-medium text-muted-foreground">
                {t('bidder.register.section.identity')}
              </legend>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field id="first" label={t('bidder.register.first')}>
                  <Input id="first" value={form.nameFirst} onChange={set('nameFirst')} required />
                </Field>
                <Field id="last" label={t('bidder.register.last')}>
                  <Input id="last" value={form.nameLast} onChange={set('nameLast')} required />
                </Field>
              </div>
            </fieldset>
            <fieldset className="space-y-3">
              <legend className="mb-1 text-xs font-medium text-muted-foreground">
                {t('bidder.register.section.contact')}
              </legend>
              <Field id="email" label={t('bidder.register.email')}>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  value={form.email}
                  onChange={set('email')}
                  required
                />
              </Field>
              <Field id="mobile" label={t('bidder.register.mobile')}>
                <Input id="mobile" value={form.mobile} onChange={set('mobile')} />
              </Field>
            </fieldset>
            <fieldset className="space-y-3">
              <legend className="mb-1 text-xs font-medium text-muted-foreground">
                {t('bidder.register.section.password')}
              </legend>
              <Field id="password" label={t('bidder.register.password')}>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  value={form.password}
                  onChange={set('password')}
                  required
                />
              </Field>
            </fieldset>

            <fieldset className="space-y-2 rounded-md border p-3">
              <legend className="px-1 text-sm text-muted-foreground">
                {t('bidder.register.consentIntro')}
              </legend>
              <Consent
                checked={aydinlatma}
                onChange={setAydinlatma}
                pre={t('bidder.register.consentAydinlatmaPre')}
                doc={t('bidder.register.consentAydinlatmaDoc')}
                post={t('bidder.register.consentAydinlatmaPost')}
                docHref="/kvkk/aydinlatma.html"
              />
              <Consent
                checked={acikRiza}
                onChange={setAcikRiza}
                pre={t('bidder.register.consentAcikRizaPre')}
                doc={t('bidder.register.consentAcikRizaDoc')}
                post={t('bidder.register.consentAcikRizaPost')}
                docHref="/kvkk/acik-riza.html"
              />
            </fieldset>

            <Turnstile onToken={onTurnstileToken} resetKey={turnstileReset} />
            {error && (
              <p
                role="alert"
                className="flex items-center gap-2 rounded-md bg-danger-tint px-3 py-2 text-sm text-danger-tint-fg"
              >
                <AlertCircle aria-hidden className="h-4 w-4 shrink-0" />
                {error}
              </p>
            )}
            {/* The one action this page exists for — the signature CTA, like the login
                submit and the header's Kayıt Ol (docs/15 §2: one per view). */}
            <Button
              type="submit"
              variant="brand"
              className="w-full"
              disabled={busy || (turnstileConfigured && !turnstileToken)}
            >
              {busy ? t('bidder.register.submitting') : t('bidder.register.submit')}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              {t('bidder.register.haveAccount')}{' '}
              <Link to="/login" className="text-tx-brand hover:underline">
                {t('bidder.register.loginLink')}
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

/**
 * One consent row: a checkbox and a sentence whose **document name is the link**.
 *
 * The sentence arrives in three pieces rather than one string because the linked span sits
 * inside it, and the split differs by language — Turkish suffixes the name (`… Metni’ni
 * okudum`), English prefixes an article (`I have read the …`). Interpolating markup into a
 * translated string is the alternative, and it is the one that ends in `dangerouslySetInnerHTML`.
 *
 * The anchor sits inside the `<label>` and needs no click guard: HTML's label activation
 * behaviour is defined to do nothing when the event targets interactive content, so opening the
 * document does not toggle the box. Verified in Chromium rather than assumed — **jsdom does not
 * implement that rule** and toggles anyway, which is why `RegisterPage.test.tsx` asserts the
 * link's name and target but not its effect on the checkbox.
 */
function Consent({
  checked,
  onChange,
  pre,
  doc,
  post,
  docHref,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  pre: string;
  doc: string;
  post: string;
  docHref: string;
}) {
  return (
    <label className="flex items-start gap-2 text-sm">
      <Checkbox
        className="mt-0.5"
        checked={checked}
        onCheckedChange={(v) => onChange(v === true)}
      />
      <span>
        {pre}
        <a
          href={docHref}
          target="_blank"
          rel="noreferrer"
          className="text-tx-brand underline underline-offset-2 hover:no-underline"
        >
          {doc}
        </a>
        {post}
      </span>
    </label>
  );
}
